import React, { useEffect, useState } from "react";
import { Save, Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import MediaInput from "@/components/admin/MediaInput";

const F = ({ label, hint, children, className = "" }) => (
  <div className={className}><label className="text-sm font-medium text-gray-700">{label}</label><div className="mt-1.5">{children}</div>{hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}</div>
);
const Card = ({ title, children }) => (
  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6"><h3 className="font-semibold text-gray-900 mb-5">{title}</h3><div className="grid sm:grid-cols-2 gap-5">{children}</div></div>
);
const PCT_OPTIONS = [20, 25, 30, 40, 50, 75, 100];

function ColorField({ label, value, onChange, testid }) {
  return (
    <F label={label}>
      <div className="flex gap-2 items-center">
        <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)} className="w-12 h-10 rounded border cursor-pointer" data-testid={testid} />
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} />
      </div>
    </F>
  );
}

export default function SettingsAdmin({ onChange }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });

  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const setSocial = (k, v) => setS((x) => ({ ...x, social: { ...x.social, [k]: v } }));
  const inp = (k, props = {}) => <Input value={s[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`setting-${k}`} {...props} />;
  const area = (k, rows = 4) => <Textarea rows={rows} value={s[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`setting-${k}`} />;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", s);
      setS(data); onChange(); toast.success("Cambios guardados y publicados");
    } catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };
  const [waTesting, setWaTesting] = useState(false);
  const [payMode, setPayMode] = useState(null);
  useEffect(() => { api.get("/admin/payment-mode").then((r) => setPayMode(r.data)).catch(() => {}); }, []);
  const testWa = async () => {
    setWaTesting(true);
    try {
      await api.put("/admin/settings", s);
      await api.post("/admin/whatsapp-alerts/test");
      toast.success("Prueba enviada. Revisa tu WhatsApp en unos segundos (y el estado en Notificaciones).");
    } catch (e) { toast.error(errMsg(e)); } finally { setWaTesting(false); }
  };
  const changePw = async () => {
    try { await api.post("/auth/change-password", pw); toast.success("Contraseña actualizada"); setPw({ current_password: "", new_password: "" }); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!s) return <Loader2 className="w-6 h-6 animate-spin brand-text" />;

  return (
    <div className="space-y-5 pb-24" data-testid="admin-settings">
      <Tabs defaultValue="brand">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="brand" data-testid="settings-tab-brand">Marca y colores</TabsTrigger>
          <TabsTrigger value="hero" data-testid="settings-tab-hero">Portada / Video</TabsTrigger>
          <TabsTrigger value="texts" data-testid="settings-tab-texts">Textos</TabsTrigger>
          <TabsTrigger value="contact" data-testid="settings-tab-contact">Contacto</TabsTrigger>
          <TabsTrigger value="social" data-testid="settings-tab-social">Redes y WhatsApp</TabsTrigger>
          <TabsTrigger value="payments" data-testid="settings-tab-payments">Pagos</TabsTrigger>
          <TabsTrigger value="security" data-testid="settings-tab-security">Seguridad</TabsTrigger>
        </TabsList>

        <TabsContent value="brand" className="mt-5 space-y-5">
          <Card title="Identidad">
            <F label="Nombre del sitio">{inp("site_name")}</F>
            <div><MediaInput label="Logo" value={s.logo_url} onChange={(v) => set("logo_url", v)} testid="logo" /></div>
          </Card>
          <Card title="Colores del diseño">
            <ColorField label="Color principal" value={s.primary_color} onChange={(v) => set("primary_color", v)} testid="color-primary" />
            <ColorField label="Color de botones" value={s.accent_color} onChange={(v) => set("accent_color", v)} testid="color-accent" />
            <ColorField label="Color secundario (sección comentarios)" value={s.secondary_color} onChange={(v) => set("secondary_color", v)} testid="color-secondary" />
            <ColorField label="Color del pie de página" value={s.footer_color} onChange={(v) => set("footer_color", v)} testid="color-footer" />
            <div className="flex items-end gap-3">
              <span className="px-4 py-2 rounded text-white text-sm" style={{ background: s.primary_color }}>Principal</span>
              <span className="px-4 py-2 rounded text-white text-sm" style={{ background: s.accent_color }}>Botón</span>
              <span className="px-4 py-2 rounded text-white text-sm" style={{ background: s.secondary_color }}>Secundario</span>
              <span className="px-4 py-2 rounded text-white text-sm" style={{ background: s.footer_color }}>Footer</span>
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="hero" className="mt-5 space-y-5">
          <Card title="Video de portada">
            <div className="sm:col-span-2"><MediaInput label="Video (MP4 subido o enlace de YouTube)" video accept="video/*" value={s.hero_video_url} onChange={(v) => set("hero_video_url", v)} testid="hero-video" /></div>
            <div className="sm:col-span-2"><MediaInput label="Imagen de respaldo (mientras carga el video)" value={s.hero_poster_url} onChange={(v) => set("hero_poster_url", v)} testid="hero-poster" /></div>
            <F label={`Oscurecer video: ${s.hero_overlay}%`} className="sm:col-span-2"><Slider value={[Number(s.hero_overlay) || 0]} max={85} step={5} onValueChange={([v]) => set("hero_overlay", v)} /></F>
          </Card>
          <Card title="Textos de portada">
            <F label="Título" className="sm:col-span-2">{inp("hero_title")}</F>
            <F label="Subtítulo">{inp("hero_subtitle")}</F>
            <F label="Texto del botón">{inp("hero_button")}</F>
          </Card>
        </TabsContent>

        <TabsContent value="texts" className="mt-5 space-y-5">
          <Card title="Sección de bienvenida">
            <F label="Título" className="sm:col-span-2">{inp("about_title")}</F>
            <F label="Texto" hint="Usa **texto** para negrita y _texto_ para cursiva" className="sm:col-span-2">{area("about_text", 5)}</F>
          </Card>
          <Card title="Encabezados de secciones">
            <F label="Excursiones - título">{inp("tours_title")}</F><F label="Excursiones - subtítulo">{inp("tours_subtitle")}</F>
            <F label="Destinos - título">{inp("destinations_title")}</F><F label="Destinos - subtítulo">{inp("destinations_subtitle")}</F>
            <F label="Comentarios - título">{inp("testimonials_title")}</F><F label="Comentarios - subtítulo">{inp("testimonials_subtitle")}</F>
            <F label="Contacto - título">{inp("contact_title")}</F><F label="Contacto - subtítulo">{inp("contact_subtitle")}</F>
            <F label="Texto del pie de página" className="sm:col-span-2">{area("footer_about", 3)}</F>
          </Card>
        </TabsContent>

        <TabsContent value="contact" className="mt-5 space-y-5">
          <Card title="Datos de contacto">
            <F label="Teléfono">{inp("phone")}</F>
            <F label="Correo público">{inp("email")}</F>
            <F label="Correo para notificaciones de compras" hint="Aquí llegarán los avisos de cada pago y mensaje">{inp("notification_email")}</F>
            <F label="Días de atención">{inp("hours_days")}</F><F label="Horario">{inp("hours_time")}</F>
            <F label="Dirección línea 1">{inp("address_line1")}</F><F label="Dirección línea 2">{inp("address_line2")}</F>
            <F label="Dirección línea 3">{inp("address_line3")}</F>
          </Card>
        </TabsContent>

        <TabsContent value="social" className="mt-5 space-y-5">
          <Card title="Botón flotante de WhatsApp">
            <F label="Número de WhatsApp" hint="Solo números con código de país. Ej: 18097656789">
              <Input value={s.whatsapp || ""} onChange={(e) => set("whatsapp", e.target.value.replace(/[^0-9]/g, ""))} placeholder="18097656789" data-testid="setting-whatsapp" />
            </F>
            <F label="Mensaje predeterminado" hint="Texto que aparece escrito cuando el cliente abre el chat">{inp("whatsapp_message")}</F>
            <label className="flex items-center gap-2 text-sm"><Switch checked={s.show_whatsapp_float !== false} onCheckedChange={(v) => set("show_whatsapp_float", v)} data-testid="toggle-whatsapp-float" />Mostrar botón flotante de WhatsApp</label>
            {s.whatsapp && <a href={`https://wa.me/${s.whatsapp}?text=${encodeURIComponent(s.whatsapp_message || "")}`} target="_blank" rel="noreferrer" className="text-sm brand-text font-medium underline">Probar enlace de WhatsApp</a>}
          </Card>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6" data-testid="wa-alerts-card">
            <h3 className="font-semibold text-gray-900">Alertas de ventas a tu WhatsApp</h3>
            <p className="text-sm text-gray-500 mt-1">Recibe un mensaje en tu celular con cada venta, comentario y mensaje de contacto (servicio gratuito CallMeBot).</p>
            <ol className="text-sm text-gray-700 mt-4 space-y-1.5 list-decimal pl-5 bg-gray-50 rounded-lg p-4 pl-9">
              <li>Guarda en tus contactos el número <b>+34 623 78 64 49</b>.</li>
              <li>Envíale por WhatsApp este mensaje exacto: <b className="font-mono">I allow callmebot to send me messages</b></li>
              <li>En 1-2 minutos te responde con tu <b>apikey</b> (un número). Pégalo abajo junto a tu número de WhatsApp.</li>
              <li>Activa el interruptor, pulsa <b>"Guardar cambios"</b> y luego <b>"Enviar prueba"</b>.</li>
            </ol>
            <div className="grid sm:grid-cols-2 gap-5 mt-5">
              <F label="Tu número de WhatsApp" hint="Con código de país, solo números. Ej: 18097656789">
                <Input value={s.wa_alert_phone || ""} onChange={(e) => set("wa_alert_phone", e.target.value.replace(/[^0-9]/g, ""))} placeholder="18097656789" data-testid="wa-alert-phone-input" />
              </F>
              <F label="Apikey de CallMeBot"><Input value={s.wa_alert_apikey || ""} onChange={(e) => set("wa_alert_apikey", e.target.value.trim())} placeholder="123456" data-testid="wa-alert-apikey-input" /></F>
              <label className="flex items-center gap-2 text-sm"><Switch checked={!!s.wa_alerts_enabled} onCheckedChange={(v) => set("wa_alerts_enabled", v)} data-testid="wa-alerts-toggle" />Alertas por WhatsApp activas</label>
              <div className="flex sm:justify-end">
                <button type="button" onClick={testWa} disabled={waTesting} className="btn-brand px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 disabled:opacity-60" data-testid="wa-alert-test-btn">
                  {waTesting && <Loader2 className="w-4 h-4 animate-spin" />}Enviar prueba
                </button>
              </div>
            </div>
          </div>
          <Card title="Redes sociales">
            {[["facebook", "Facebook", "https://facebook.com/tu-pagina"], ["instagram", "Instagram", "https://instagram.com/tu-usuario"], ["tiktok", "TikTok", "https://tiktok.com/@tu-usuario"], ["youtube", "YouTube", "https://youtube.com/@tu-canal"], ["x", "X (Twitter)", "https://x.com/tu-usuario"]].map(([k, label, ph]) => (
              <F key={k} label={label} hint="Déjalo vacío para ocultarlo"><Input value={s.social?.[k] || ""} placeholder={ph} onChange={(e) => setSocial(k, e.target.value.trim())} data-testid={`social-${k}-input`} /></F>
            ))}
            <label className="flex items-center gap-2 text-sm sm:col-span-2"><Switch checked={s.show_social_float !== false} onCheckedChange={(v) => set("show_social_float", v)} data-testid="toggle-social-float" />Mostrar botón flotante de redes sociales</label>
            <p className="text-xs text-gray-500 sm:col-span-2">Estas redes aparecen en el botón flotante, en la barra superior y en el pie de página. Recuerda pulsar "Guardar cambios".</p>
          </Card>
        </TabsContent>

        <TabsContent value="payments" className="mt-5 space-y-5">
          <Card title="Opciones de pago para clientes">
            <F label="Porcentajes que el cliente puede pagar por adelantado" className="sm:col-span-2">
              <div className="flex flex-wrap gap-4">
                {PCT_OPTIONS.map((p) => (
                  <label key={p} className="flex items-center gap-2 text-sm"><Checkbox data-testid={`deposit-option-${p}`} checked={(s.deposit_options || []).includes(p)}
                    onCheckedChange={(c) => set("deposit_options", c ? [...(s.deposit_options || []), p].sort((a, b) => a - b) : (s.deposit_options || []).filter((x) => x !== p))} />{p}%</label>
                ))}
              </div>
            </F>
            {payMode && (payMode.live ? (
              <div className="sm:col-span-2 rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800" data-testid="payment-mode-live">
                Stripe en <b>MODO REAL (producción)</b>: los clientes pagan con tarjetas reales y el dinero llega a tu cuenta de Stripe. Las tarjetas de prueba (4242...) ya no funcionan.
              </div>
            ) : (
              <div className="sm:col-span-2 rounded-lg bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800" data-testid="payment-mode-test">
                Stripe en <b>MODO DE PRUEBA</b>: no se cobra dinero real. Tarjeta de prueba: 4242 4242 4242 4242, cualquier fecha futura y CVC.
              </div>
            ))}
          </Card>
        </TabsContent>

        <TabsContent value="security" className="mt-5">
          <Card title="Cambiar contraseña de administrador">
            <F label="Contraseña actual"><Input type="password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} /></F>
            <F label="Nueva contraseña"><Input type="password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} /></F>
            <div><button onClick={changePw} className="btn-brand px-4 py-2 rounded-md font-medium flex items-center gap-2"><KeyRound className="w-4 h-4" />Actualizar</button></div>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="fixed bottom-6 right-6 z-30">
        <button onClick={save} disabled={saving} data-testid="save-settings-btn" className="btn-accent px-6 h-12 rounded-full font-semibold shadow-xl flex items-center gap-2 disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Guardar cambios
        </button>
      </div>
    </div>
  );
}
